/**
 * Credit Error Handler
 * Handles errors related to expired credits and provides user-friendly messages
 */

import toast from 'react-hot-toast';

/**
 * Check if an error is related to expired credits
 * @param {Error} error - The error object
 * @returns {boolean} - True if error is related to expired credits
 */
export const isExpiredCreditError = (error) => {
  if (!error) return false;
  
  const status = error.response?.status;
  const message = error.response?.data?.message || error.message || '';
  
  // Check for 403 status and expired credit message
  return status === 403 && (
    message.toLowerCase().includes('expired') ||
    message.toLowerCase().includes('expiration') ||
    message.toLowerCase().includes('credit has expired')
  );
};

/**
 * Handle expired credit errors with appropriate user feedback
 * @param {Error} error - The error object
 * @param {Function} t - Translation function
 * @returns {boolean} - True if error was handled, false otherwise
 */
export const handleExpiredCreditError = (error, t = (text) => text) => {
  if (isExpiredCreditError(error)) {
    const message = error.response?.data?.message || 'This credit has expired. Please contact support or purchase a new credit to continue.';
    
    toast.error(t(message), {
      duration: 6000,
      position: 'top-center',
    });
    
    return true;
  }
  
  return false;
};

/**
 * Get user-friendly error message for credit-related errors
 * @param {Error} error - The error object
 * @param {Function} t - Translation function
 * @returns {string} - User-friendly error message
 */
export const getCreditErrorMessage = (error, t = (text) => text) => {
  if (!error) return t('An unknown error occurred');
  
  const status = error.response?.status;
  const message = error.response?.data?.message || error.message || '';
  
  if (isExpiredCreditError(error)) {
    return t('This credit has expired. Please contact support or purchase a new credit to continue.');
  }
  
  if (status === 403) {
    return t('You do not have permission to access this resource. Please check your credit status.');
  }
  
  if (status === 404) {
    return t('Credit not found. It may have been revoked or deleted.');
  }
  
  if (message) {
    return message;
  }
  
  return t('An error occurred while processing your credit. Please try again or contact support.');
};

/**
 * Wrap an async function with expired credit error handling
 * @param {Function} fn - The async function to wrap
 * @param {Function} t - Translation function
 * @returns {Function} - Wrapped function with error handling
 */
export const withExpiredCreditHandling = (fn, t = (text) => text) => {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      if (handleExpiredCreditError(error, t)) {
        throw error; // Re-throw after handling to allow caller to handle as well
      }
      throw error;
    }
  };
};

